import * as React from 'react';
import { z } from 'zod';
import { FormModal, RHFTextField, RHFSelect } from '../components/form';
import Typography from '@mui/material/Typography';
import { Container, Table, tableCellClasses, Button, Box, IconButton, Grid, Select, MenuItem, Alert, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { fetchRepairReplacementHistories, updateRepairReplacementHistory, fetchCustomers, fetchVehicles, fetchSolutions } from "../services/ApiService";
import DataTable from '../components/DataTable';

const editRecordSchema = z.object({
  comment: z.string().min(1, "Required"),
  status: z.string().min(1, "Required"),
  reason: z.string().optional(),
}).superRefine((d, ctx) => {
  if (d.status === "Not Clear" && !d.reason) ctx.addIssue({ path: ["reason"], message: "Required", code: "custom" });
});

export function SalesCoordinatorRepairReplacementCommentHistory() {
  const [devicesOptions, setDevicesOptions] = React.useState([])
  const [haulier, setHauliers] = React.useState([])
  const [devices, setDevices] = React.useState([])
  const [trucks, setTrucks] = React.useState([])
  const [employees, setEmployees] = React.useState([])
  const [modalOpen, setModalOpen] = React.useState(false);
  const [chatHistoryOpen, setChatHistoryOpen] = React.useState(false);
  const [chatHistoryRecords, setChatHistoryRecords] = React.useState([]);
  const [editModalOn, setEditModalOn] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });
  const [selectedSale, setSelectedSale] = React.useState(null);
  const [saleModalOpen, setSaleModalOpen] = React.useState(false);
  const [selectedCustomerSupport, setSelectedCustomerSupport] = React.useState(null);
  const [csModalOpen, setCSModalOpen] = React.useState(false);
  const [selectedTechnical, setSelectedTechnical] = React.useState(null);
  const [technicalModalOpen, setTechnicalModalOpen] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [haulierOptions, setHaulierOptions] = React.useState([]);
  const [truckOptions, setTruckOptions] = React.useState([]);

  React.useEffect(() => {
    fetchHauliers();
    fetchDevices()
    fetchTruck();
    fetchDeliveries();
  }, []);

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDateForInput = (datetimeStr) => {
    if (!datetimeStr) return "";
    const date = new Date(datetimeStr);
    return date.toISOString().slice(0, 16);
  };

  const fetchDeliveries = async () => {
    try {
      const response = await fetchRepairReplacementHistories();
      console.log(response.data);
      setEmployees(response.data);
    } catch (error) {
      const errorMessage = error.response?.statusText || error.message || 'Error fetching hauliers';
    }
  };

  const fetchHauliers = async () => {
    try {
      const response = await fetchCustomers();
      const haulierDict = response.data.reduce((acc, item) => {
        acc[item.id] = item.name;
        return acc;
      }, {});
      const haulier = response.data.reduce((acc, item) => {
        acc[item.id] = item.id;
        return acc;
      }, {});
      setHaulierOptions(haulierDict);
      setHauliers(haulier);
    } catch (error) {
      const errorMessage = error.response?.statusText || error.message || 'Error fetching hauliers';
      console.error("Error fetching hauliers:", error);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleStatusChange = async (saleId, newStatus, sale) => {
    const updatedSale = { ...sale, status: newStatus };
    try {
      await updateRepairReplacementHistory(saleId, updatedSale);
      setEmployees((prevData) =>
        prevData.map((item) =>
          item.id === saleId ? { ...item, status: newStatus } : item
        )
      );
      showSnackbar('Status updated successfully', 'success');
    } catch (error) {
      console.error('Failed to update status:', error);
      showSnackbar('Failed to update status', 'error');
    }
  };

  const fetchTruck = async () => {
    try {
      const response = await fetchVehicles();
      console.log(response.data);
      const allNames = response.data.reduce((acc, item) => {
        acc[item.id] = item.registration_number;
        return acc;
      }, {});
      const haulierDict = response.data.reduce((acc, item) => {
        acc[item.id] = item.name;
        return acc;
      }, {});
      setTruckOptions(haulierDict)
      setTrucks(allNames);
    } catch (error) {
      const errorMessage = error.response?.statusText || error.message || 'Error fetching hauliers';
    }
  };

  const handleEditOn = (row) => {
    setEditModalOn(true)
    setSelectedRow(row)
  }

  const handleEditOff = () => { setEditModalOn(false); }

  const fetchDevices = async () => {
    try {
      const response = await fetchSolutions();
      const allNames = response.data.map((employee) => employee.name);
      const deviceDict = response.data.reduce((acc, item) => {
        acc[item.id] = item.name;
        return acc;
      }, {});
      const device = response.data.reduce((acc, item) => {
        acc[item.id] = item.cost;
        return acc;
      }, {});
      setDevicesOptions(deviceDict)
      setDevices(device);
    } catch (error) {
      const errorMessage = error.response?.statusText || error.message || 'Error fetching hauliers';
    }
  };

  const handleExport = () => {
    const doc = new jsPDF("l", "mm", "a3");
    doc.setFontSize(18);
    doc.text("Collection Recovery New Installation", 14, 16);
    const columns = [
      "ID", "Sale", "Customer Support", "Discount", "Discounted Cost",
      "Remarks", "Type", "Duration", "Billing Date", "Sale Status",
      "Customer Support Status", "Technical Status", "Overall Status",
    ];
    const tableData = employees.map((row) => [
      row.id, row.sale, row.customer_support, row.discount, row.discountedCost,
      row.remarks, row.type, row.duration, formatDateForInput(row.billing_date),
      row.sale_details?.status || "", row.sale_details?.customer_support_status || "",
      row.technical_status || "", row.status,
    ]);
    doc.autoTable({
      head: [columns], body: tableData, startY: 30,
      styles: { fontSize: 9, cellPadding: 3 }, theme: "grid",
      didDrawPage: () => { doc.setFontSize(18); doc.text("Collection Recovery New Installation Report", 14, 16); },
    });
    doc.save("Collection_Recovery_New_Installation_Report.pdf");
  };

  const EditEmployeeModal = ({ selectedRow, open, handleClose }) => {
    return (
      <FormModal
        open={open}
        onClose={handleClose}
        title="Edit Record"
        schema={editRecordSchema}
        defaultValues={{
          comment: selectedRow?.comment ?? "",
          status: selectedRow?.status ?? "Clear",
          reason: selectedRow?.reason ?? "",
        }}
        onSubmit={async (data) => {
          try {
            await updateRepairReplacementHistory(selectedRow.id, data);
            showSnackbar("Updated successfully", "success");
            fetchDeliveries();
          } catch {
            showSnackbar("Failed to update", "error");
            throw new Error();
          }
        }}
        submitLabel="Update"
        width={600}
      >
        {({ control, watch }) => {
          const status = watch("status");
          return (
            <Grid container spacing={2.5}>
              <Grid item xs={12}><RHFTextField control={control} name="comment" label="Comment" multiline rows={3} required /></Grid>
              <Grid item xs={12} md={6}>
                <RHFSelect control={control} name="status" label="Status" required>
                  <MenuItem value="Clear">Clear</MenuItem>
                  <MenuItem value="Not Clear">Not Clear</MenuItem>
                </RHFSelect>
              </Grid>
              {status === "Not Clear" && (
                <Grid item xs={12}><RHFTextField control={control} name="reason" label="Reason" multiline rows={2} /></Grid>
              )}
            </Grid>
          );
        }}
      </FormModal>
    );
  };

  const handleDelete = async (id) => {
    try {
      await updateRepairReplacementHistory(id, { deleted: true });
      showSnackbar("Customer deleted successfully", "success");
    } catch (error) {
      showSnackbar("Failed to delete customer", "error");
      console.error(error);
    }
  };

  const handleOpenChatHistory = (installationId) => {
    const allRecords = employees.filter(row => row.installation === installationId);
    setChatHistoryRecords(allRecords);
    setChatHistoryOpen(true);
  };

  const handleCloseChatHistory = () => {
    setChatHistoryOpen(false);
    setChatHistoryRecords([]);
  };

  return (
    <Container maxWidth="xl" sx={{ m: 2 }}>
      {editModalOn && selectedRow && (
        <EditEmployeeModal selectedRow={selectedRow} open={editModalOn} handleClose={handleEditOff} />
      )}
      <DataTable
        title="Collection Recovery Repair/Replacement"
        columns={[
          { key: 'id', label: 'ID' },
          { key: 'installation', label: 'Installation ID' },
          { key: 'installation_detail', label: 'Installation Detail' },
          { key: 'comment', label: 'Comment' },
          { key: 'status', label: 'Status' },
          { key: 'reason', label: 'Reason', render: (r) => r.reason || '-' },
          { key: 'added_by', label: 'Added By' },
          { key: 'added_by_name', label: 'Added By Name' },
          { key: 'created_at', label: 'Created At', render: (r) => new Date(r.created_at).toLocaleString() },
        ]}
        rows={Object.values(employees.reduce((acc, row) => {
          const existing = acc[row.installation];
          if (!existing || new Date(row.created_at) > new Date(existing.created_at)) acc[row.installation] = row;
          return acc;
        }, {}))}
        toolbarActions={<Button variant="contained" color="primary" onClick={handleExport}>Export to PDF</Button>}
        renderActions={(row) => (
          <Button size="small" variant="outlined" onClick={() => handleOpenChatHistory(row.installation)}>
            View Chat History
          </Button>
        )}
      />
      <Dialog open={chatHistoryOpen} onClose={handleCloseChatHistory} maxWidth="md" fullWidth>
        <DialogTitle>Chat History for Installation</DialogTitle>
        <DialogContent>
          {chatHistoryRecords.length === 0 ? (
            <Typography>No chat history found for this installation.</Typography>
          ) : (
            <DataTable
              columns={[
                { key: 'id', label: 'ID' },
                { key: 'installation_detail', label: 'Installation Detail' },
                { key: 'comment', label: 'Comment' },
                { key: 'status', label: 'Status' },
                { key: 'reason', label: 'Reason', render: (r) => r.reason || '-' },
                { key: 'added_by_name', label: 'Added By Name' },
                { key: 'created_at', label: 'Created At', render: (r) => new Date(r.created_at).toLocaleString() },
              ]}
              rows={chatHistoryRecords}
              searchable={false}
              rowsPerPageOptions={[10, 25]}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChatHistory} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={saleModalOpen} onClose={() => setSaleModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Sale Details</DialogTitle>
        <DialogContent>
          {selectedSale ? (
            <Box>
              <Typography variant="body1"><strong>Customer:</strong> {haulierOptions[selectedSale.customer]}</Typography>
              <Typography variant="body1"><strong>Solution:</strong> {devicesOptions[selectedSale.solution]}</Typography>
              <Typography variant="body1"><strong>Corporate:</strong> {selectedSale.corporate}</Typography>
              <Typography variant="body1"><strong>POC Name:</strong> {selectedSale.poc_name}</Typography>
              <Typography variant="body1"><strong>POC Contact:</strong> {selectedSale.poc_contact}</Typography>
              <Typography variant="body1"><strong>Plan:</strong> {selectedSale.plan}</Typography>
              <Typography variant="body1"><strong>Unit:</strong> {selectedSale.unit}</Typography>
            </Box>
          ) : (
            <Typography variant="body2">Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaleModalOpen(false)} color="primary">Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={csModalOpen} onClose={() => setCSModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Customer Support Details</DialogTitle>
        <DialogContent>
          {selectedCustomerSupport ? (
            <Box>
              <Typography variant="body1"><strong>Vehicle:</strong> {selectedCustomerSupport.vehicle_details?.name}</Typography>
              <Typography variant="body1"><strong>Location:</strong> {selectedCustomerSupport.location}</Typography>
            </Box>
          ) : (
            <Typography variant="body2">Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCSModalOpen(false)} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={technicalModalOpen} onClose={() => setTechnicalModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Technical Details</DialogTitle>
        <DialogContent>
          {selectedTechnical ? (
            <Box>
              <Typography variant="body1"><strong>IMEI:</strong> {selectedTechnical.imei_details?.imei}</Typography>
            </Box>
          ) : (
            <Typography variant="body2">Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTechnicalModalOpen(false)} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
